import { Button } from "$components/button/button";
import { Dialog } from "$components/dialog/dialog";
import { QrCodeIcon, ReissuePinIcon } from "$components/icons";
import { PopoverMenu } from "$components/popover-menu/popover-menu";
import { useUser } from "$global-providers/user-provider";
import { linkToCardReissue } from "$routes/card/reissue/links";
import { linkToEditPatient } from "$routes/edit/patient/links";
import { ReissuePinModal } from "$routes/patient/[:patientId]/_lib/patient-info-dropdown/reissue-pin-modal";
import { useOngoingInpatientVisitsQuery } from "$routes/patient/[:patientId]/inpatient/_lib/__generated__/ongoing-inpatient-visits.gql";
import {
  linkToPatientInpatient,
  linkToPatientInpatientAdmit,
} from "$routes/patient/[:patientId]/inpatient/links";
import { styled, Typography } from "@material-ui/core";
import ContactPhoneIcon from "@material-ui/icons/ContactPhone";
import EditIcon from "@material-ui/icons/Edit";
import HotelIcon from "@material-ui/icons/Hotel";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { ContactPointSystem, Role } from "elephant-web/gql";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { constructFullName, type PatientCentrePatient } from ".";

export const ThreeDotMenu: React.FC<{ patient: PatientCentrePatient }> = ({
  patient,
}) => {
  const navigate = useNavigate();
  const user = useUser();
  const { formatMessage } = useIntl();

  const { data: ongoingInpatientData } = useOngoingInpatientVisitsQuery({
    variables: { input: { patientUuid: patient.uuid, offset: 0, limit: 1 } },
  });
  const inpatientVisitId =
    ongoingInpatientData?.ongoingInpatientVisits?.items[0]?._id;

  const [reissuePinModalOpen, setReissuePinModalOpen] = useState(false);
  const [patientDetailsModalOpen, setPatientDetailsModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const primaryMenuItems = [
    ...(user.role !== Role.Sonographer
      ? [
          {
            id: "patient.menu.admit",
            defaultMessage: "Admit patient",
            icon: <HotelIcon />,
            onClick: () => {
              if (inpatientVisitId) {
                navigate(
                  linkToPatientInpatient(patient.uuid, inpatientVisitId),
                );
              } else {
                navigate(
                  linkToPatientInpatientAdmit({ patientUuid: patient.uuid }),
                );
              }
            },
          },
        ]
      : []),
    {
      id: "patient.menu.edit",
      defaultMessage: "Edit patient",
      icon: <EditIcon />,
      onClick: () => navigate(linkToEditPatient(patient.uuid)),
    },
    {
      id: "patient.menu.details",
      defaultMessage: "See patient details",
      icon: <ContactPhoneIcon />,
      onClick: () => setPatientDetailsModalOpen(true),
    },
  ];

  const secondaryMenuItems =
    user.role === Role.Sonographer
      ? []
      : [
          {
            id: "patient.menu.reissueCard",
            defaultMessage: "Re-issue card",
            icon: <QrCodeIcon />,
            onClick: () => navigate(linkToCardReissue(), { state: patient }),
          },
          {
            id: "patient.menu.reissuePin",
            defaultMessage: "Re-issue pin",
            icon: <ReissuePinIcon />,
            onClick: () => setReissuePinModalOpen(true),
          },
        ];

  return (
    <>
      <Button
        circular
        variant="outlined"
        aria-label={formatMessage({
          id: "patient.centre.threeDotMenu.ariaLabel",
          defaultMessage: "Patient actions",
        })}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
          setIsMenuOpen(true);
        }}
        data-testid="patient-centre-three-dot-menu"
      >
        <MoreVertIcon />
      </Button>
      <PopoverMenu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        primaryMenuItems={primaryMenuItems}
        secondaryMenuItems={secondaryMenuItems}
      />
      <ReissuePinModal
        onClose={() => setReissuePinModalOpen(false)}
        open={reissuePinModalOpen}
        patient={{
          uuid: patient.uuid,
          contactPoints: patient.person.contactPoints[0]
            ? [
                {
                  system: ContactPointSystem.Phone,
                  value: patient.person.contactPoints[0].value,
                  ownership: patient.person.contactPoints[0].ownership,
                },
              ]
            : [],
          fullName: constructFullName(patient.person),
          dob: patient.person.dob,
        }}
      />
      <PatientDetailsModal
        patient={patient}
        open={patientDetailsModalOpen}
        onClose={() => setPatientDetailsModalOpen(false)}
      />
    </>
  );
};

const PatientDetailsDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    width: "80%",
    maxWidth: "512px",
  },
});

const PatientDetailsContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  padding: "20px",
  backgroundColor: theme.palette.grey[100],
  borderRadius: "8px",
}));

const PatientDetailsContentItem = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const PatientDetailsContentItemTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const PatientDetailsContentItemValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 700,
}));

const PatientDetailsFooter = styled("div")({
  display: "flex",
  justifyContent: "space-between",
});

const PatientDetailsModal: React.FC<{
  patient: PatientCentrePatient;
  open: boolean;
  onClose: () => void;
}> = ({ patient, open, onClose }) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const details = [
    {
      titleId: "patient.details.phone",
      defaultMessage: "Personal phone number",
      value: patient.person.contactPoints[0]?.value || "-",
    },
    {
      titleId: "patient.details.address",
      defaultMessage: "Address",
      value: `${patient.person.addresses[0]?.street || ""}, ${
        patient.person.addresses[0]?.state || ""
      }`,
    },
    {
      titleId: "patient.details.nationality",
      defaultMessage: "Nationality",
      value: patient.person.nationality,
    },
    {
      titleId: "patient.details.language",
      defaultMessage: "Language",
      value: "English",
    },
    {
      titleId: "patient.details.nextOfKin",
      defaultMessage: "Next of Kin",
      value: patient.person.contacts[0]?.name.fullname || "-",
    },
    {
      titleId: "patient.details.elephantId",
      defaultMessage: "Elephant ID",
      value: patient.cardId || "-",
    },
  ];

  return (
    <PatientDetailsDialog
      open={open}
      onCancel={onClose}
      title={formatMessage({
        id: "patient.menu.details",
        defaultMessage: "Patient details",
      })}
    >
      <PatientDetailsContent>
        {details.map((detail) => (
          <PatientDetailsContentItem key={detail.titleId}>
            <PatientDetailsContentItemTitle variant="body1">
              {formatMessage({
                id: detail.titleId,
                defaultMessage: detail.defaultMessage,
              })}
            </PatientDetailsContentItemTitle>
            <PatientDetailsContentItemValue variant="body1">
              {detail.value}
            </PatientDetailsContentItemValue>
          </PatientDetailsContentItem>
        ))}
      </PatientDetailsContent>
      <PatientDetailsFooter>
        <Button
          variant="text"
          color="primary"
          onClick={() => navigate(linkToEditPatient(patient.uuid))}
        >
          {formatMessage({
            id: "patient.details.edit",
            defaultMessage: "Edit patient",
          })}
        </Button>
        <Button variant="contained" color="primary" onClick={onClose}>
          {formatMessage({
            id: "patient.details.done",
            defaultMessage: "Done",
          })}
        </Button>
      </PatientDetailsFooter>
    </PatientDetailsDialog>
  );
};
